/* eslint-disable react-hooks/exhaustive-deps */
import * as React from 'react';
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import NotFound from 'screen/notFound';
import AppLayout from './AppLayout';
import SummerProgramsLayout from './SummerProgramsLayout';
import DashBoardLayout from './DashBoardLayout';
import ProtectedRoute from './ProtectedRoute';
import UserLayout from './UserLayout';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { get } from 'lodash';
import { routers } from './router';
import Button from 'react-bootstrap/esm/Button';
import { STRIPE_SUBSCRIPTION_STATUS } from 'constants/common';
import { getCurrentBasicDetail, logout } from 'app/basicInformationSlice/basicInformationSlice';
import { getUserId, getAccessToken, getUserRole } from 'utils/sessionStorage';
import { getStudent } from 'app/authorization/authorizationSlice';
import { getStripeSubscriptionInfo } from 'app/basicInformationSlice/basicInformationSlice';
import websocketService from 'services/websocketService';
// import counselorServices from 'services/counselorServices';
import { ChatListProvider } from 'components/layout/ChatListContext';
import { ChatBoxManagerProvider, useChatBoxManager } from 'components/ChatBoxManager';
import { UnreadCountsProvider } from 'components/UnreadCountsContext';
import CamOAuthRedirectHandler from 'components/CamOAuthRedirectHandler';
import CamOAuthNavigationBridge from 'components/CamOAuthNavigationBridge';
import { ensureCamOAuthLoginRoute } from 'utils/camAuth';
import UpgradePaymentLayout from './UpgradePaymentLayout';
import { Container } from 'react-bootstrap';
import Header from 'components/layout/header';
import MobileHeader from 'components/layout/MobileHeader';
import SideBar from 'components/layout/sideBar';
import { goToTrialEndedPayment, isCanceledTrial } from 'utils/trialPayment';

// Chrome (Header + SideBar) for the unauthenticated-bypass '/payment/upgrade-plan' route.
// This route intentionally sits OUTSIDE ProtectedRoute (see history on the Route below),
// so this wrapper must not add any subscription-based redirect logic — it only adds the
// visual layout that the rest of the app has (header + sidebar).
const UpgradePlanChrome: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Container fluid className="vh-100 overflow-hidden px-0" style={{ position: 'relative' }}>
    <div className="d-none d-sm-block w-100">
      <Header title="Upgrade your Plan" />
    </div>
    <div className="d-block d-sm-none w-100">
      <MobileHeader />
    </div>
    <div className="gm-content">
      {/* hideList: this page has no list-menu navigation state (unlike Dashboard/Application),
          so we use the same slim icon-only rail the rest of the app uses for simple pages
          (Settings, Support, Payment) — matches DashBoardLayout's non-dashboard branch. */}
      <div className="d-none d-sm-block gm-sidebar">
        <SideBar hideList />
      </div>
      <div className="gm-main-content">
        {children}
      </div>
    </div>
  </Container>
);

const TrialExpiresDialog = ({ open, status, trialCount, studentId, studentEmail, planName, onClose, onLogout}: any) => {
  const history = useHistory();
  const [isRedirecting, setIsRedirecting] = React.useState(false);
  let title = '';
  switch (status) {
      case STRIPE_SUBSCRIPTION_STATUS.canceled:
        if (trialCount>0)
          title = "Sorry, it looks like your trial has ended. <br/>Please complete your charge to continue using GradMap.";
        else
          title = 'Your subscription has been canceled!';
        break;
      case STRIPE_SUBSCRIPTION_STATUS.incomplete:
        title = 'Your subscription is incomplete!';
        break;
      case STRIPE_SUBSCRIPTION_STATUS.incomplete_expired:
        title =  'Your subscription has expired!';
        break;
      case STRIPE_SUBSCRIPTION_STATUS.past_due:
        title =  'Your subscription is past due!';
        break;
      case STRIPE_SUBSCRIPTION_STATUS.paused:
        title =  'Your subscription is paused!';
        break;
      case STRIPE_SUBSCRIPTION_STATUS.unpaid:
        title =  'Your trial has expired!';
        break;
      default:
        title =  'Your subscription has expired!';
        break;
  }

  // Bug fix: the Stripe Billing Portal only manages a still-alive subscription
  // (active/past_due/...) — it has nothing to charge for a subscription that is
  // already fully `canceled` (which is exactly what a trial that ended with no
  // payment method used to do, see stripe.service.ts). So for that case we now
  // create a brand new Checkout Session instead (trialDays: 0 — the student
  // already used their trial, this is a straight paid re-subscribe), which
  // requires a payment method and shows the "Add promotion code" field.
  // The Billing Portal path is kept as the flow for every other non-canceled
  // status (past_due, unpaid, paused, ...), where the subscription is still
  // alive and the Portal can act on it.
  // Logic moved to utils/trialPayment.ts so the header "Upgrade" link can reuse
  // the exact same flow when the trial has ended.
  const handleContinueOnStripe = async () => {
    if (isRedirecting) return;
    setIsRedirecting(true);
    await goToTrialEndedPayment({ status, trialCount, planName, studentId, studentEmail, history });
    setIsRedirecting(false);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <img
          src={'/assets/images/logo-color.svg'}
          alt="GradMap logo"
          style={{ alignSelf: 'center' }}
          height={48}
          className="d-inline pointer"
        />
      </DialogTitle>
      <DialogContent>
        <div className="text-center py-5">
          <h4 dangerouslySetInnerHTML={{__html: title}}></h4>
        </div>
      </DialogContent>

      <DialogActions className="d-flex justify-content-between align-items-center px-4 pb-3">
        {/* Temporary dismiss — lets the student go back into GradMap to check
            their info, or log out, without forcing an immediate Stripe visit. */}
        <div>
          <Button
            onClick={onClose}
            variant="outline-secondary"
            className="p-2 px-4 me-2"
          >
            Close
          </Button>
          <Button
            onClick={onLogout}
            variant="link"
            className="p-2 px-2 text-muted"
          >
            Logout
          </Button>
        </div>

        {isCanceledTrial(status, trialCount) ? (
          <Button
              onClick={handleContinueOnStripe}
              disabled={isRedirecting}
              variant="warning"
              className="p-2 px-5"
            >
              {isRedirecting ? 'REDIRECTING...' : 'GO TO PAYMENT'}
          </Button>
        ):
        (
          <Button
            onClick={() => {
              history.push(routers.settings.path + '?active-tab=5');
            }}
              variant="primary"
              className="p-2 px-5"
            >
            Go to billing
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// Component to handle logout cleanup (must be inside ChatBoxManagerProvider)
// This component also exposes a way to reset initialization refs
const LogoutCleanup: React.FC<{ onLogout?: () => void }> = ({ onLogout }) => {
  const { closeAllChatBoxes } = useChatBoxManager();
  
  // Monitor logout by checking if myBasicInformation becomes null
  const myBasicInformation = useAppSelector((store: any) => store.basicInformation.myBasicInformation);
  const prevBasicInfoRef = React.useRef(myBasicInformation);

  React.useEffect(() => {
    // If we had user info before and now it's null, user logged out
    if (prevBasicInfoRef.current && !myBasicInformation) {
      // Close all chatboxes
      closeAllChatBoxes();
      // Cleanup WebSocket: leave session and disconnect
      websocketService.cleanupOnLogout();
      // Call onLogout callback to reset initialization refs
      if (onLogout) {
        onLogout();
      }
    }
    prevBasicInfoRef.current = myBasicInformation;
  }, [myBasicInformation, closeAllChatBoxes, onLogout]);

  return null;
};

const RouterConfig = () => {
  React.useLayoutEffect(() => {
    ensureCamOAuthLoginRoute();
  }, []);
  const [stripeStatus, setStripeStatus] = React.useState('');
  const [trialCount, setTrialCount] = React.useState(0);
  // trial_count starts at its default (0) until fetchStudentInfo resolves. The
  // dialog's own title logic needs trial_count to tell "trial ended" (canceled +
  // trialCount>0) apart from a plain cancellation (trialCount===0) — if the
  // dialog opens (via the OTHER effect, driven by Redux stripeSubscriptionInfo,
  // which can be populated by header.tsx's own independent fetch) before this
  // resolves, it renders the wrong "Your subscription has been canceled!" /
  // "Go to billing" message for a split second, then flips to the correct
  // "trial has ended" / "CONTINUE ON STRIPE" once trial_count arrives. Gate the
  // dialog open on this being loaded so it never shows the wrong text first.
  const [trialCountLoaded, setTrialCountLoaded] = React.useState(false);
  const [showDialog, setShowDialog] = React.useState(false);
  const [studentEmail, setStudentEmail] = React.useState('');
  const [isLoadingInfo, setIsLoadingInfo] = React.useState(false);
  const dispatch:any = useAppDispatch(); 
  const history = useHistory();
  let location = useLocation();
  const personalStore:any = useAppSelector((store:any) => store.basicInformation.myBasicInformation);
  const stripeSubscriptionInfo = useAppSelector((store:any) => store.basicInformation.stripeSubscriptionInfo);
  const studentId = personalStore?.student_id || getUserId();
  // Use ref to track if we've already initialized to prevent multiple calls
  const hasInitializedRef = React.useRef(false);
  const isInitializingRef = React.useRef(false);
  // Cache counselors to avoid calling API multiple times
  const counselorsCacheRef = React.useRef<{ studentId: string; counselors: any[] } | null>(null);
  
  // Reset initialization refs when user logs out
  const handleLogoutCleanup = React.useCallback(() => {
    hasInitializedRef.current = false;
    isInitializingRef.current = false;
    counselorsCacheRef.current = null;
    // Clear the PREVIOUS student's cached subscription/dialog state — otherwise
    // it briefly (or, if the next fetch fails, indefinitely) shows for whoever
    // logs in next on this same tab (e.g. TrialExpiresDialog popping up wrongly).
    setStripeStatus('');
    setTrialCount(0);
    setTrialCountLoaded(false);
    setShowDialog(false);
    setStudentEmail('');
  }, []);

  const handleLogout = () => {
    // Clear dialog state SYNCHRONOUSLY, right here — dispatch(logout()) is an
    // async thunk (awaits an API call + clearAuthData()) and we navigate to
    // /public/login immediately after, without awaiting it. Relying only on
    // LogoutCleanup (which fires once the thunk resolves and Redux updates)
    // left a window where the OLD student's TrialExpiresDialog was still
    // showing "open" on the login page itself, stuck until something else
    // happened to re-render.
    setStripeStatus('');
    setTrialCount(0);
    setShowDialog(false);
    setStudentEmail('');
    try {
      dispatch(logout());
    }
    catch(ex){
      console.log('Error:', ex);
    }
    history.push(routers.login.path);
  };

  // Check Redux store for subscription status on mount and when it changes
  React.useEffect(() => {
    const token = getAccessToken();
    if (!token) return;

    // If Redux store already has subscription info, use it immediately
    if (stripeSubscriptionInfo && !stripeStatus) {
      const statusFromStore = get(stripeSubscriptionInfo, 'payload.subcription_status', '') || 
                              get(stripeSubscriptionInfo, 'subcription_status', '');
      if (statusFromStore) {
        setStripeStatus(statusFromStore);
      }
    }
  }, [stripeSubscriptionInfo]);

  // Fetch student information from API
  const fetchStudentInfo = React.useCallback(async (userId: string) => {
    const res = await dispatch(getStudent(userId)).unwrap();
    setTrialCount(res?.trial_count ?? 0);
    setStudentEmail(res?.email ?? '');
    setTrialCountLoaded(true);
    return res;
  }, [dispatch]);

  // Fetch stripe subscription information
  const fetchStripeSubscriptionInfo = React.useCallback(async (studentId: string) => {
    const subStripeInfo: any = await dispatch(getStripeSubscriptionInfo(studentId));
    if (subStripeInfo) {
      const status = get(subStripeInfo, 'payload.subcription_status', '');
      setStripeStatus(status);
    }
    return subStripeInfo;
  }, [dispatch]);

  // Parents/counselors do not own a subscription — never show the global paywall
  // dialog for their own login. Per-child gating happens when they open a student.
  const isPartnerRoleWithoutOwnSubscription = React.useCallback(() => {
    const role = (getUserRole() || personalStore?.user_role || '').toLowerCase().trim();
    return role === 'parent' || role === 'counselor';
  }, [personalStore?.user_role]);

  // Determine if subscription dialog should be shown
  const shouldShowSubscriptionDialog = React.useCallback((stripeStatus: string, token: string) => {
    if (!stripeStatus) return false;
    if (isPartnerRoleWithoutOwnSubscription()) return false;
    
    const isLogin = !!token;
    const isActiveOrTrialing = [STRIPE_SUBSCRIPTION_STATUS.active, STRIPE_SUBSCRIPTION_STATUS.trialing].includes(stripeStatus);
    const isExcludedPath = location.pathname.includes('/public') || // login/register/etc — never show a paywall on an unauthenticated page
                            location.pathname.includes('/settings') ||
                            location.pathname.includes('/support') ||
                            location.pathname.includes('/payment') ||
                            location.pathname.includes('/success') ||
                            location.pathname.includes('/payment/upgrade-plan') ||
                            location.pathname.includes('/payment/upgrade-success') ||
                            location.pathname.includes('/dashboard/upgrade'); // 🌟 new

    return isLogin && !isActiveOrTrialing && !isExcludedPath;
  }, [location.pathname, isPartnerRoleWithoutOwnSubscription]);

  // Join student session room (session = session_studentId)
  const joinStudentSessionRoom = React.useCallback(async (studentId: string) => {
    try {
      const studentIdNum = Number(studentId);
      await websocketService.joinSession(studentIdNum);
      sessionStorage.setItem('room_id', `session_${studentId}`);
      console.log('Successfully joined session room');
    } catch (error) {
      console.error('Error joining session room:', error);
      throw error;
    }
  }, []);

  // Check and join session for students (session = session_studentId, no counselor required)
  const checkAndJoinCounselorSession = React.useCallback(async (studentId: string) => {
    const userRole = getUserRole()?.toLowerCase()?.trim();

    if (!userRole) {
      console.log('No user role found in sessionStorage');
      return;
    }

    if (userRole === 'counselor') {
      console.log('Counselor detected - session will be managed through student selection menu');
      return;
    }

    if (userRole !== 'student') {
      console.log(`User role "${userRole}" does not require session joining`);
      return;
    }

    if (websocketService.hasJoinedSessionRoom()) {
      const currentSession = websocketService.getCurrentSessionData();
      if (currentSession?.student_id === Number(studentId)) {
        console.log('Already joined this session:', currentSession);
        return;
      }
    }

    try {
      await websocketService.connect();
      await joinStudentSessionRoom(studentId);
    } catch (error) {
      console.error('Error joining session room:', error);
    }
  }, [joinStudentSessionRoom]);

  // Main function to get user information
  const getInfo = React.useCallback(async () => {
    // Prevent concurrent calls
    if (isInitializingRef.current) {
      return;
    }

    isInitializingRef.current = true;
    setIsLoadingInfo(true);

    try {
      const token = getAccessToken();
      const userId = getUserId();
      
      if (!token || !userId) {
        throw new Error('Missing token or userId');
      }

      // Fetch student information
      const studentInfo = await fetchStudentInfo(userId);
      const studentIdFromResponse = studentInfo?.student_id || userId;

      // Populate myBasicInformation (required for role-based UI: parent, counselor, student)
      await dispatch(getCurrentBasicDetail(studentIdFromResponse));

      // Fetch stripe subscription information
      const subStripeInfo = await fetchStripeSubscriptionInfo(studentIdFromResponse);
      
      // Determine if subscription dialog should be shown
      const stripeStatus = subStripeInfo 
        ? get(subStripeInfo, 'payload.subcription_status', '')
        : '';
      
      if (location.pathname.includes('/payment')) {
        setShowDialog(false);
      } else {
        const shouldShow = shouldShowSubscriptionDialog(stripeStatus, token);
        setShowDialog(shouldShow);
      }

      // Check and join counselor session for protected routes
      if (!location.pathname.startsWith('/public')) {
        await checkAndJoinCounselorSession(studentIdFromResponse);
      }

      // Mark as initialized after successful call
      hasInitializedRef.current = true;
    } catch (ex) {
      // Backend will handle invalid tokens, no need to auto logout
      console.error('Error getting info:', ex);
      // Reset refs on error so we can retry if needed
      hasInitializedRef.current = false;
    } finally {
      isInitializingRef.current = false;
      setIsLoadingInfo(false);
    }
  }, [fetchStudentInfo, fetchStripeSubscriptionInfo, shouldShowSubscriptionDialog, checkAndJoinCounselorSession, location.pathname]);

  // Initialize user info only once when app loads
  React.useEffect(() => {
    // Skip if already initialized or currently initializing
    if (hasInitializedRef.current || isInitializingRef.current) {
      return;
    }

    const token = getAccessToken();
    const userId = getUserId();
    
    // Only initialize if we have token and userId, and haven't initialized yet
    if (!token || !userId) {
      return;
    }

    // Skip if we're on public routes (login, register, etc.)
    if (location.pathname.startsWith('/public')) {
      return;
    }

    getInfo();
  }, [getInfo, location.pathname]);

  // Helper function to get current subscription status
  const getCurrentSubscriptionStatus = React.useCallback(() => {
    if (stripeSubscriptionInfo) {
      return get(stripeSubscriptionInfo, 'payload.subcription_status', '') ||
             get(stripeSubscriptionInfo, 'subcription_status', '');
    }
    return stripeStatus;
  }, [stripeSubscriptionInfo, stripeStatus]);

  // Helper function to get the current (or most recently held) plan name —
  // needed so TrialExpiresDialog can re-create a Checkout Session for the same
  // plan the student was trialing once their subscription is fully canceled.
  const getCurrentSubscriptionPlan = React.useCallback(() => {
    if (stripeSubscriptionInfo) {
      return get(stripeSubscriptionInfo, 'payload.subcription_plan', '') ||
             get(stripeSubscriptionInfo, 'subcription_plan', '');
    }
    return '';
  }, [stripeSubscriptionInfo]);

  // Sync subscription status from Redux store when it changes
  React.useEffect(() => {
    if (stripeSubscriptionInfo) {
      const statusFromStore = get(stripeSubscriptionInfo, 'payload.subcription_status', '') || 
                              get(stripeSubscriptionInfo, 'subcription_status', '');
      if (statusFromStore && statusFromStore !== stripeStatus) {
        setStripeStatus(statusFromStore);
      }
    }
  }, [stripeSubscriptionInfo]);

  // Update dialog visibility when location changes or subscription status changes
  React.useEffect(() => {
    if (isPartnerRoleWithoutOwnSubscription()) {
      setShowDialog(false);
      return;
    }

    // Get current status from Redux store or local state
    const currentStatus = stripeSubscriptionInfo 
      ? (get(stripeSubscriptionInfo, 'payload.subcription_status', '') || 
         get(stripeSubscriptionInfo, 'subcription_status', ''))
      : stripeStatus;
    
    // Never show the paywall on an unauthenticated page (login/register/etc) —
    // e.g. right after clicking Logout, before the async logout thunk/token
    // clear has actually finished, this pathname check is what stops the
    // PREVIOUS student's dialog from being stuck open on the login screen.
    if (location.pathname.includes('/public')) {
      setShowDialog(false);
      return;
    }

    if (currentStatus) {
      const isLogin = !!getAccessToken();
      const shouldShow = isLogin &&
        trialCountLoaded && // wait for trial_count so the dialog never opens with the wrong "canceled" (no-trial) wording, then flips
        ![STRIPE_SUBSCRIPTION_STATUS.active, STRIPE_SUBSCRIPTION_STATUS.trialing].includes(currentStatus) &&
        !(location.pathname.includes('/settings') || location.pathname.includes('/support') ||
          location.pathname.includes('/payment') || location.pathname.includes('/success') ||
          // location.pathname.includes('/payment/upgrade-plan') ||
          // location.pathname.includes('/payment/upgrade-success') ||
          location.pathname.includes('/dashboard/upgrade')
        );
      
      if (location.pathname.includes('/payment') ||
          location.pathname.includes('/dashboard/upgrade')){
        setShowDialog(false);
      } else {
        setShowDialog(shouldShow);
      }
    } else {
      // If no status yet, hide dialog
      setShowDialog(false);
    }
  }, [location.pathname, stripeStatus, stripeSubscriptionInfo, isPartnerRoleWithoutOwnSubscription, trialCountLoaded]);

  console.log('isLoadingInfo:', isLoadingInfo);

  return (
    <UnreadCountsProvider>
      <ChatListProvider>
        <ChatBoxManagerProvider>
          <CamOAuthRedirectHandler />
          <CamOAuthNavigationBridge />
          <LogoutCleanup onLogout={handleLogoutCleanup} />
          {(() => {
            const currentStatus = getCurrentSubscriptionStatus();
            return currentStatus ? (
              <TrialExpiresDialog
                open={showDialog}
                status={currentStatus}
                trialCount={trialCount}
                studentId={studentId}
                studentEmail={studentEmail}
                planName={getCurrentSubscriptionPlan()}
                onClose={() => setShowDialog(false)}
                onLogout={handleLogout}
              />
            ) : null;
          })()}
          <Switch>
            <Route path="/public" component={(props: any) => <UserLayout {...props} />} />
            <Route
              path={routers.upgradePlan.path}
              exact
              render={(props: any) => (
                <UpgradePlanChrome>
                  <routers.upgradePlan.component {...props} />
                </UpgradePlanChrome>
              )}
            />
            <ProtectedRoute path={["/application"]}>
              <AppLayout />
            </ProtectedRoute>
            <ProtectedRoute path="/summer-programs">
              <SummerProgramsLayout />
            </ProtectedRoute>
            <ProtectedRoute path={[
                '/payment/upgrade-plan',
                '/payment/upgrade-success',
              ]}>
              <UpgradePaymentLayout />
            </ProtectedRoute>
            <ProtectedRoute path={[
              '/dashboard', 
              '/dashboard/children', 
              '/dashboard/upgrade', 
              '/dashboard/student/:id', 
              // '/payment/upgrade-plan',
              // '/payment/upgrade-success',
              '/support', 
              '/settings', 
              '/payment',
              '/success'
              ]}>
              <DashBoardLayout />
            </ProtectedRoute>
            <Route exact path="/">
              <Redirect to="/dashboard" />
            </Route>
            <Route>
              <NotFound />
            </Route>
          </Switch>
        </ChatBoxManagerProvider>
      </ChatListProvider>
    </UnreadCountsProvider>
  );
};

export default RouterConfig;
